import type { ScorecardSection as ScorecardData, SectionValue, SessionType } from "../../../../shared/analysis-types";
import { ScorecardSection } from "./scorecard-section";
import { SectionErrorFallback } from "@/components/narrative/section-error-fallback";
import { useAnalysisCatalog, type AlwaysListItemDef } from "./use-analysis-catalog";

interface AlwaysListScorecardProps {
  data: SectionValue<ScorecardData>;
  sessionType: SessionType;
  promptVersion: string;
  className?: string;
}

function matchesSessionType(def: AlwaysListItemDef, sessionType: SessionType): boolean {
  if (def.appliesTo === "both") return true;
  return def.appliesTo === sessionType;
}

export function AlwaysListScorecard({
  data,
  sessionType,
  promptVersion,
  className,
}: AlwaysListScorecardProps) {
  const { catalog } = useAnalysisCatalog(promptVersion);

  if ("error" in data) {
    return (
      <div className={className}>
        <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
          Always List
        </h3>
        <div className="mt-4">
          <SectionErrorFallback sectionName="always_list_scorecard" />
        </div>
      </div>
    );
  }

  // Gate on the catalog so labels resolve before first paint, matching the
  // legacy synchronous registry (no raw-id label flash).
  if (!catalog) return null;

  const registryMap = new Map(catalog.alwaysList.map((def) => [def.id, def]));

  const resolved = data.items
    .filter((item) => {
      if (item.rating === "na") return false;
      const def = registryMap.get(item.id);
      if (!def) return true;
      return matchesSessionType(def, sessionType);
    })
    .map((item) => ({
      id: item.id,
      rating: item.rating as Exclude<typeof item.rating, "na">,
      label: registryMap.get(item.id)?.label ?? item.id,
      summary: item.summary,
      evidence: item.evidence,
      what_to_try: item.what_to_try,
      why_it_matters: item.why_it_matters,
      copy_paste_reinforcement: item.copy_paste_reinforcement,
    }));

  return (
    <ScorecardSection
      title="Always List"
      items={resolved}
      className={className}
    />
  );
}
